import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { RequireRole } from "@/lib/useUser";
import AdminSidebar from "./AdminSidebar";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/notifications";
import { CalendarSyncButton } from "@/components/calendar";
import { SignOutButton } from "@/components/layout/SignOutButton";

export default function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  
  // Extract page title from path
  const getPageTitle = () => {
    const path = location.pathname.split("/").pop() || "overview";
    return path
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <RequireRole allowed={["admin", "staff"]}>
      <div className="min-h-screen bg-gray-900 text-white flex font-geist">
        {/* Mobile Sidebar Overlay */}
        <div
          className={cn(
            "fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity",
            isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          onClick={() => setIsSidebarOpen(false)}
        />

        {/* Sidebar - Hidden on mobile, visible on desktop */}
        <div
          className={cn(
            "fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:transform-none",
            isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          )}
        >
          <AdminSidebar />
        </div>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-screen overflow-hidden">
          {/* Mobile Header */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900 sticky top-0 z-30">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-white hover:bg-gray-800"
            >
              {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            <h1 className="text-lg font-semibold capitalize">{getPageTitle()}</h1>
            <div className="flex items-center gap-3">
              <CalendarSyncButton />
              <NotificationBell />
              <SignOutButton />
            </div>
          </header>

          {/* Desktop Header */}
          <header className="hidden lg:flex items-center justify-between px-6 py-3 border-b border-gray-800 bg-gray-900">
            <h1 className="text-xl font-semibold capitalize">{getPageTitle()}</h1>
            <div className="flex items-center gap-3">
              <CalendarSyncButton />
              <NotificationBell />
              <SignOutButton />
            </div>
          </header>

          {/* Page Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </RequireRole>
  );
}
